/**
 * PokerBountyPanel — Admin subpanel for the Daily Knockout Bounty system.
 * Nested inside PokerAdminPanel under a new "Bounties" tab.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { RefreshCw, Target, X, Skull, DollarSign } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ withCredentials: true });
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtTs = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(undefined, { timeZone: 'America/Chicago' }); } catch { return iso; }
};

export default function PokerBountyPanel() {
    const [bounties, setBounties] = useState([]);
    const [status, setStatus] = useState('active');
    const [form, setForm] = useState({ target_user_id: '', amount: 25, hours: 24, note: '' });
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(
                `${API}/poker-room/admin/bounties?status=${status}`, auth(),
            );
            setBounties(res.data.bounties || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        } finally { setLoading(false); }
    }, [status]);

    useEffect(() => { load(); }, [load]);

    const create = async () => {
        if (!form.target_user_id.trim()) { toast.error('Target user ID required'); return; }
        if (!form.amount || form.amount <= 0) { toast.error('Amount must be positive'); return; }
        try {
            await axios.post(`${API}/poker-room/admin/bounties`, form, auth());
            toast.success('Bounty posted!');
            setForm({ target_user_id: '', amount: 25, hours: 24, note: '' });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        }
    };
    const cancel = async (bid) => {
        try {
            await axios.delete(`${API}/poker-room/admin/bounties/${bid}`, auth());
            toast.success('Bounty cancelled');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        }
    };

    return (
        <div className="space-y-4" data-testid="poker-admin-bounties">
            {/* Create form */}
            <div className="rounded-xl border border-red-500/40 bg-gradient-to-br from-red-950/30 to-black/40 p-3">
                <div className="flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-red-300" />
                    <span className="font-black text-sm text-red-200 uppercase tracking-widest">Post a Knockout Bounty</span>
                </div>
                <p className="text-[10px] text-white/50 mb-3">
                    When the target is busted (loses their whole stack at a table), the player who knocks them out claims the bounty. Bots that KO the target still burn the bounty (returned to house). Expires after N hours if unclaimed.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
                    <div className="md:col-span-2">
                        <label className="text-[10px] uppercase tracking-widest text-white/60 font-black">Target Mongo User ID</label>
                        <Input value={form.target_user_id}
                               onChange={(e) => setForm({ ...form, target_user_id: e.target.value })}
                               placeholder="e.g. 69dab6d…"
                               data-testid="poker-bounty-target" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-white/60 font-black">Amount ($)</label>
                        <Input type="number" min="1" step="1" value={form.amount}
                               onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
                               data-testid="poker-bounty-amount" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-white/60 font-black">Hours</label>
                        <Input type="number" min="1" max="168" value={form.hours}
                               onChange={(e) => setForm({ ...form, hours: Number(e.target.value) })}
                               data-testid="poker-bounty-hours" />
                    </div>
                </div>
                <Textarea value={form.note}
                          onChange={(e) => setForm({ ...form, note: e.target.value })}
                          placeholder="Optional flair text (e.g. 'Weekend Warrior Bounty!')"
                          className="mb-2"
                          data-testid="poker-bounty-note" />
                <Button onClick={create} className="bg-red-600 hover:bg-red-500"
                        data-testid="poker-bounty-create">
                    <Target className="w-3 h-3 mr-1" /> Post Bounty
                </Button>
            </div>

            {/* Status filter + list */}
            <div className="flex items-center justify-between">
                <div className="flex gap-1">
                    {['active', 'claimed', 'superseded', 'cancelled'].map((s) => (
                        <button key={s} onClick={() => setStatus(s)}
                                className={`px-2 py-1 rounded-md text-[10px] uppercase font-black tracking-widest ${
                                    status === s ? 'bg-red-500 text-black' : 'bg-black/40 text-white/50'
                                }`}
                                data-testid={`poker-bounty-filter-${s}`}>
                            {s}
                        </button>
                    ))}
                </div>
                <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
                    <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
            </div>

            <div className="rounded-xl border border-white/10 overflow-hidden">
                <table className="w-full text-xs">
                    <thead className="bg-black/40 text-white/50 uppercase tracking-widest">
                        <tr>
                            <th className="text-left px-2 py-2">Target</th>
                            <th className="text-right px-2 py-2">Amount</th>
                            <th className="text-left px-2 py-2">Status</th>
                            <th className="text-left px-2 py-2">Claimed By</th>
                            <th className="text-left px-2 py-2">Created</th>
                            <th className="text-left px-2 py-2">Expires</th>
                            <th className="px-2 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {bounties.length === 0 && (
                            <tr><td colSpan="7" className="text-center text-white/40 py-6 italic">
                                No bounties in this status.
                            </td></tr>
                        )}
                        {bounties.map((b) => (
                            <tr key={b._id} className="border-t border-white/5" data-testid={`poker-bounty-row-${b._id}`}>
                                <td className="px-2 py-1.5 font-bold">{b.target_name}</td>
                                <td className="px-2 py-1.5 text-right text-emerald-300 font-black">{money(b.amount)}</td>
                                <td className="px-2 py-1.5">
                                    <Badge className={`text-[9px] ${
                                        b.status === 'active' ? 'bg-red-500/40 text-red-100'
                                        : b.status === 'claimed' ? 'bg-emerald-500/40 text-emerald-100'
                                        : 'bg-white/10 text-white/60'
                                    }`}>{b.status}</Badge>
                                </td>
                                <td className="px-2 py-1.5">
                                    {b.claimed_by_name ? (
                                        <span className="flex items-center gap-1">
                                            <Skull className="w-3 h-3 text-red-400" />
                                            {b.claimed_by_name}
                                            {b.claimed_by_is_bot && <span className="text-white/40">(bot)</span>}
                                        </span>
                                    ) : '—'}
                                </td>
                                <td className="px-2 py-1.5 text-white/50 text-[10px]">{fmtTs(b.created_at)}</td>
                                <td className="px-2 py-1.5 text-white/50 text-[10px]">{fmtTs(b.expires_at)}</td>
                                <td className="px-2 py-1.5 text-right">
                                    {b.status === 'active' && (
                                        <Button variant="ghost" size="sm" onClick={() => cancel(b._id)}
                                                data-testid={`poker-bounty-cancel-${b._id}`}>
                                            <X className="w-3 h-3" />
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="text-[10px] text-white/40 flex items-center gap-1">
                <DollarSign className="w-3 h-3" />
                Bounty amount is paid directly to the KO'er's wallet at hand completion.
            </div>
        </div>
    );
}
